import React, { useState, useMemo } from 'react';

/* ============================================================================
   SectionDataPanel — Per-Section Data Provenance
   ============================================================================
   Merged right-side panel that shows exactly which data points were used to
   generate the currently-selected proposal section.

   Uses section.data_sources (new proposals) for precise filtering.
   Falls back to section.source_workers matching (legacy proposals).
   ============================================================================ */

const workerLabel = (type) => {
  const labels = {
    origination: 'Origination',
    regulatory_structuring: 'Regulatory & Structuring',
    technology: 'Technology',
    distribution: 'Distribution',
  };
  return labels[type] || type?.replace(/_/g, ' ');
};

const verdictColor = (v) => {
  if (v === 'greenlight' || v === 'pass' || v === 'proceed') return 'bg-green-100 text-green-700';
  if (v === 'conditional') return 'bg-yellow-100 text-yellow-700';
  if (v === 'reject' || v === 'fail') return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-600';
};

const toTitleCase = (str) =>
  str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/* ---- Recursive data value renderer ---- */
const DataValue = ({ value, depth = 0 }) => {
  if (value === null || value === undefined) return <span className="text-gray-400 italic">—</span>;
  if (typeof value === 'boolean') return <span className={value ? 'text-green-700' : 'text-red-700'}>{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'number') return <span className="text-primary-700 font-medium">{value}</span>;
  if (typeof value === 'string') return <span className="text-gray-700">{value}</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400 italic">none</span>;
    if (value.every((v) => typeof v !== 'object' || v === null)) {
      return <span className="text-gray-700">{value.join(', ')}</span>;
    }
    return (
      <div className="space-y-1.5 mt-1">
        {value.map((item, i) => (
          <div key={i} className="bg-white/60 rounded p-1.5 border border-gray-100">
            <DataValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    return (
      <div className={`space-y-1 ${depth > 0 ? '' : 'mt-1'}`}>
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex flex-col">
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{toTitleCase(k)}</span>
            <div className="text-xs pl-1"><DataValue value={v} depth={depth + 1} /></div>
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-gray-500 dark:text-gray-400">{String(value)}</span>;
};

/* ---- Collapsible group ---- */
const CollapsibleGroup = ({ title, count, defaultOpen = false, children, badge, icon }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasContent = count == null || count > 0;
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between py-2.5 px-4 text-left transition-colors ${hasContent ? 'hover:bg-gray-50/50' : 'opacity-40 cursor-default'}`}
        disabled={!hasContent}
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-400">{icon}</span>}
          <svg
            className={`w-3 h-3 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">{title}</span>
          {count != null && <span className="text-[10px] text-gray-400">({count})</span>}
        </div>
        {badge}
      </button>
      {open && hasContent && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
};

/* ---- Worker detail with optional field highlighting ---- */
const WorkerDetail = ({ worker, highlightFields }) => {
  const [showOutput, setShowOutput] = useState(false);
  const confidence = worker.confidence_score != null ? parseFloat(worker.confidence_score) : null;

  // If highlightFields given, filter output_data to only those fields
  const filteredOutput = useMemo(() => {
    if (!worker.output_data) return null;
    if (!highlightFields || highlightFields.length === 0) return worker.output_data;
    const filtered = {};
    for (const field of highlightFields) {
      if (worker.output_data[field] !== undefined) {
        filtered[field] = worker.output_data[field];
      }
    }
    return Object.keys(filtered).length > 0 ? filtered : worker.output_data;
  }, [worker.output_data, highlightFields]);

  const fieldCount = filteredOutput ? Object.keys(filteredOutput).length : 0;
  const isFiltered = highlightFields && highlightFields.length > 0;

  return (
    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-800">{workerLabel(worker.worker_type)}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${verdictColor(worker.verdict)}`}>
            {worker.verdict}
          </span>
        </div>
        {confidence != null && (
          <div className="flex items-center gap-1.5">
            <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-primary-500 rounded-full" style={{ width: `${confidence * 100}%` }} />
            </div>
            <span className="text-[10px] text-gray-500 font-medium">{(confidence * 100).toFixed(0)}%</span>
          </div>
        )}
      </div>

      {/* Output data — show only relevant fields when data_sources available */}
      {filteredOutput && fieldCount > 0 && (
        <div>
          <button
            onClick={() => setShowOutput(!showOutput)}
            className="text-[10px] font-medium text-primary-600 hover:text-primary-800 transition-colors flex items-center gap-1"
          >
            <svg className={`w-3 h-3 transition-transform ${showOutput ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {isFiltered ? `${fieldCount} relevant fields` : `output_data (${fieldCount} fields)`}
          </button>
          {showOutput && (
            <div className="mt-1.5 bg-white border border-gray-200 rounded-lg p-3 text-xs max-h-64 overflow-y-auto">
              <DataValue value={filteredOutput} />
            </div>
          )}
        </div>
      )}

      {/* Reasoning */}
      {worker.reasoning && (
        <div className="bg-white border border-gray-200 rounded-lg p-2.5 max-h-28 overflow-y-auto">
          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Reasoning</p>
          <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{worker.reasoning}</p>
        </div>
      )}

      {/* KB entities */}
      {worker.kb_entities_used && worker.kb_entities_used.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {worker.kb_entities_used.map((entity, i) => (
            <span key={i} className="px-1.5 py-0.5 bg-primary-50 text-primary-600 rounded text-[10px]">
              {entity.category ? `${entity.category}:${entity.name || ''}` : entity.name || entity.key || JSON.stringify(entity).slice(0, 30)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ==== Main Panel ==== */
const SectionDataPanel = ({ section, workers = [], proposal, questionnaire = [] }) => {
  // === Extract data_sources (new proposals) or fall back to source_workers (legacy) ===
  const ds = section?.data_sources || null;
  const sourceWorkers = section?.source_workers || [];
  const hasProvenance = ds !== null;

  // Internal sections from proposal
  const internalSections = Array.isArray(proposal?.internal_sections) ? proposal.internal_sections : [];
  const deSection = internalSections.find((s) => s.section_number === 15);
  const erSection = internalSections.find((s) => s.section_number === 16);
  const deContent = deSection?.content && typeof deSection.content === 'object' ? deSection.content : null;
  const erContent = erSection?.content && typeof erSection.content === 'object' ? erSection.content : null;

  // --- A. WORKERS: filter + highlight ---
  const matchingWorkers = useMemo(() => {
    if (!section) return [];
    if (hasProvenance && ds.worker_fields && Object.keys(ds.worker_fields).length > 0) {
      return workers.filter((w) => w.worker_type in ds.worker_fields);
    }
    return workers.filter((w) => sourceWorkers.includes(w.worker_type));
  }, [section, workers, ds, hasProvenance, sourceWorkers]);

  const workerFieldMap = hasProvenance && ds.worker_fields ? ds.worker_fields : {};

  // --- B. QUESTIONNAIRE: filter by referenced IDs ---
  const filteredQuestionnaire = useMemo(() => {
    if (!section) return [];
    if (hasProvenance && ds.questionnaire_ids && ds.questionnaire_ids.length > 0) {
      const idSet = new Set(ds.questionnaire_ids.map(String));
      return questionnaire.filter((q) => idSet.has(String(q.question_id)));
    }
    return questionnaire;
  }, [section, questionnaire, ds, hasProvenance]);

  // --- C. DECISION ENGINE: filter checks + risk flags ---
  const filteredChecks = useMemo(() => {
    if (!section) return [];
    const allChecks = deContent?.checks_summary || [];
    if (hasProvenance && ds.decision_checks && ds.decision_checks.length > 0) {
      const checkSet = new Set(ds.decision_checks.map((c) => c.toLowerCase()));
      return allChecks.filter((c) => checkSet.has((c.check || '').toLowerCase()));
    }
    return allChecks;
  }, [section, deContent, ds, hasProvenance]);

  const filteredRiskFlags = useMemo(() => {
    if (!section) return [];
    const allFlags = deContent?.risk_flags || [];
    if (hasProvenance && ds.risk_flags && ds.risk_flags.length > 0) {
      return ds.risk_flags
        .filter((idx) => idx >= 0 && idx < allFlags.length)
        .map((idx) => allFlags[idx]);
    }
    return allFlags;
  }, [section, deContent, ds, hasProvenance]);

  const deItemCount = filteredChecks.length + filteredRiskFlags.length;

  // --- D. EXTERNAL RESEARCH: filter by indices ---
  const filteredResearch = useMemo(() => {
    if (!section) return [];
    const allResults = erContent?.results || [];
    if (hasProvenance && ds.research_indices && ds.research_indices.length > 0) {
      return ds.research_indices
        .filter((idx) => idx >= 0 && idx < allResults.length)
        .map((idx) => allResults[idx]);
    }
    return allResults;
  }, [section, erContent, ds, hasProvenance]);

  // --- E. ASSUMPTIONS: from data_sources ---
  const assumptions = hasProvenance && ds.assumptions ? ds.assumptions : [];

  // Placeholder when no section selected
  if (!section) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <p className="text-sm text-gray-400">Click a section to view its data sources</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Panel header */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Data Provenance</p>
            <p className="text-sm font-semibold text-gray-800 mt-0.5">
              {section.section_number}. {section.title}
            </p>
          </div>
          {hasProvenance && (
            <span className="px-1.5 py-0.5 bg-green-50 text-green-600 rounded text-[9px] font-medium">Traced</span>
          )}
        </div>
      </div>

      {/* A. Worker Analysis */}
      <CollapsibleGroup
        title="Worker Analysis"
        count={matchingWorkers.length}
        defaultOpen={false}
        icon={
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
      >
        {matchingWorkers.length > 0 ? (
          <div className="space-y-3">
            {matchingWorkers.map((w) => (
              <WorkerDetail
                key={w.id || w.worker_type}
                worker={w}
                highlightFields={workerFieldMap[w.worker_type] || null}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">No workers mapped to this section</p>
        )}
      </CollapsibleGroup>

      {/* B. Questionnaire Inputs */}
      <CollapsibleGroup
        title="Questionnaire Inputs"
        count={filteredQuestionnaire.length}
        defaultOpen={false}
        icon={
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      >
        <div className="max-h-52 overflow-y-auto space-y-2">
          {filteredQuestionnaire.map((q, i) => (
            <div key={i} className="bg-gray-50 rounded p-2">
              <p className="text-[10px] text-gray-400 font-medium">Q{q.question_id}: {q.question_text}</p>
              <p className="text-xs text-gray-800 font-medium mt-0.5">{q.response_text}</p>
            </div>
          ))}
        </div>
      </CollapsibleGroup>

      {/* C. Decision Engine */}
      <CollapsibleGroup
        title="Decision Engine"
        count={deItemCount}
        defaultOpen={false}
        badge={deContent?.result ? (
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${verdictColor(deContent.result)}`}>
            {deContent.result}
          </span>
        ) : null}
        icon={
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      >
        <div className="space-y-2">
          {filteredChecks.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold text-gray-500 uppercase">Checks</p>
              {filteredChecks.map((check, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${verdictColor(check.result)}`}>
                    {check.result}
                  </span>
                  <span className="text-xs text-gray-700">{check.check}</span>
                </div>
              ))}
            </div>
          )}
          {filteredRiskFlags.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold text-gray-500 uppercase">Risk Flags</p>
              {filteredRiskFlags.map((flag, i) => (
                <p key={i} className={`text-xs px-2 py-1 rounded ${
                  flag.severity === 'high' ? 'bg-red-50 text-red-700' :
                  flag.severity === 'medium' ? 'bg-yellow-50 text-yellow-700' :
                  'bg-blue-50 text-blue-700'
                }`}>
                  {flag.flag}
                </p>
              ))}
            </div>
          )}
          {deItemCount === 0 && (
            <p className="text-xs text-gray-400 italic">No decision engine checks relevant to this section</p>
          )}
        </div>
      </CollapsibleGroup>

      {/* D. External Research */}
      <CollapsibleGroup
        title="External Research"
        count={filteredResearch.length}
        defaultOpen={false}
        icon={
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        }
      >
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {filteredResearch.map((r, i) => (
            <div key={i} className="bg-gray-50 rounded p-2">
              <p className="text-xs font-medium text-gray-800">{r.research_point || r.title}</p>
              {r.summary && <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">{r.summary}</p>}
              {r.source && <p className="text-[10px] text-gray-400 mt-0.5">Source: {r.source}</p>}
              {r.findings && r.findings.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {r.findings.slice(0, 3).map((f, j) => (
                    <p key={j} className="text-[10px] text-gray-600">
                      {f.title && <span className="font-medium">{f.title}: </span>}
                      {f.key_data}
                      {f.source && <span className="text-gray-400"> [{f.source}]</span>}
                    </p>
                  ))}
                </div>
              )}
              {r.relevance_to_workers && (
                <div className="flex gap-1 mt-1 flex-wrap">
                  {r.relevance_to_workers.map((w, j) => (
                    <span key={j} className="px-1.5 py-0.5 bg-primary-50 text-primary-600 rounded text-[10px]">
                      {w.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
          {filteredResearch.length === 0 && (
            <p className="text-xs text-gray-400 italic">No external research cited for this section</p>
          )}
        </div>
      </CollapsibleGroup>

      {/* E. Assumptions (only for new proposals with data_sources) */}
      {assumptions.length > 0 && (
        <CollapsibleGroup
          title="Assumptions"
          count={assumptions.length}
          defaultOpen={false}
          icon={
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
        >
          <div className="space-y-1">
            {assumptions.map((a, i) => (
              <p key={i} className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">{a}</p>
            ))}
          </div>
        </CollapsibleGroup>
      )}

      {/* Key Metrics from section */}
      {section.key_metrics && section.key_metrics.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/30">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Key Metrics</p>
          <div className="grid grid-cols-2 gap-2">
            {section.key_metrics.map((m, i) => (
              <div key={i} className="bg-white rounded p-2 border border-gray-100">
                <p className="text-[10px] text-gray-400">{m.label}</p>
                <p className="text-xs font-semibold text-gray-800">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionDataPanel;
